import { App, Button, Form, Input } from 'antd';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiErrorEnum } from '../../../enums/ApiErrorEnum';
import { FoldersService } from '../../../services/foldersApiService';
import { useAppStore } from '../../../store/store';
import { getApiErrorCode } from '../../../utils/apiErrorCode';
import { resolveFolderPasswordApiErrorMessage } from '../../../utils/folderPasswordApiErrors';
import { Icon } from '../../Core/Icon';
import ResetFilePasswordForm from '../ResetFilePasswordForm';

const foldersService = new FoldersService();

/**
 * Interface defining the props for EnterFilePasswordForm component.
 */
interface EnterFilePasswordFormProps {
  content?: ReactNode; // Additional content to display above the form.
  fileId: number; // File ID to validate password for.
  /** Callback fired on successful password verification (session is saved in store). */
  onSubmit: () => void;
  /** When set, password is verified but session is NOT saved; this callback receives the password (e.g. for remove-password flow). */
  onSubmitWithPassword?: (password: string) => void;
  /** When set, password is verified but session is NOT saved; this callback is called with no args (e.g. for edit-password: verify then open edit modal without unlocking folder). */
  onSubmitAfterVerify?: () => void;
  onCancel?: () => void; // Optional callback for cancellation.
  confirmLabel?: string; // Text for confirm button, falls back to i18n default.
  cancelLabel?: string; // Text for cancel button, falls back to i18n default.
  passwordName?: string; // Form field name for password, defaults to 'password'.
  passwordPlaceholder?: string; // Placeholder text for password input.
  autoFocus?: boolean; // Should autofocus password input on mount.
  /** When true, show "Forgot password?" link that opens reset-via-OTP modal (only for folder owner). */
  isOwner?: boolean;
  /** Called when reset-password modal succeeds; use to close the parent (enter-password) modal. */
  onCloseParentModal?: () => void;
}

/**
 * EnterFilePasswordForm displays a password input for protected files (e.g. folders), with lockout and error messaging.
 * Handles form validation, API password verification, lockout, session saving, and error feedback.
 */
function EnterFilePasswordForm({
  content,
  fileId,
  onSubmit,
  onSubmitWithPassword,
  onSubmitAfterVerify,
  onCancel,
  confirmLabel,
  cancelLabel,
  passwordName = 'password',
  passwordPlaceholder,
  autoFocus = true,
  isOwner = false,
  onCloseParentModal,
}: EnterFilePasswordFormProps) {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [form] = Form.useForm();

  // Get modal loading state and handlers from Zustand
  const isModalLoading = useAppStore((s) => s.isModalLoading);
  const setIsModalLoading = useAppStore((s) => s.setIsModalLoading);

  // File password lockout info and handlers from Zustand
  const { filePasswordLockoutInfo, setFilePasswordLockoutInfo, clearFilePasswordLockoutInfo } =
    useAppStore();

  // Local error state for displaying error messages
  const [error, setError] = useState<string | null>(null);

  // Ref to password input for focusing/selecting
  const passwordInputRef = useRef<any>(null);

  // Determine if the file is currently locked out (rate-limited)
  const isLockedOut =
    filePasswordLockoutInfo?.fileId === fileId && filePasswordLockoutInfo.minutesRemaining > 0;

  // Reset error and fields whenever fileId changes
  useEffect(() => {
    setError(null);
    form.resetFields();
  }, [fileId, form]);

  /**
   * Handles form submission:
   * 1. Validates fields.
   * 2. Attempts password verification via API.
   * 3. Saves session in store or displays proper error message / handles lockout.
   * 4. Calls onSubmit if successful.
   */
  const handleConfirm = useCallback(async () => {
    try {
      // Validate form fields with Ant Design
      const values = await form.validateFields();
      setError(null);
      setIsModalLoading(true);

      try {
        // Attempt to verify folder password via API
        const passwordValue = values[passwordName];
        const response = await foldersService.verifyFolderPassword(fileId, {
          password: passwordValue,
        });

        clearFilePasswordLockoutInfo();

        if (onSubmitWithPassword) {
          // Caller wants the password back (e.g. for remove-password); do not save session
          onSubmitWithPassword(passwordValue);
        } else if (onSubmitAfterVerify) {
          // Verify only, do not save session (e.g. for edit-password: open edit modal without unlocking)
          onSubmitAfterVerify();
        } else {
          // Save authenticated session for folder in Zustand store (one entry per verify)
          const storeState = useAppStore.getState();
          storeState.setFilePasswordSession({
            expiresAt: response.expiresAt,
            fileId: response.folderId,
            sessionToken: response.sessionToken,
          });
          onSubmit();
        }
      } catch (err: any) {
        // Handle error codes for authentication and lockout
        if (err.status === 401) {
          setError(t('modal.enterFolderPasswordForm.error')); // Invalid password
        } else if (
          err.response?.errorCode === ApiErrorEnum.LockedOut ||
          err.response?.name === ApiErrorEnum.FolderPasswordLockout ||
          err.response?.value?.errorCode === ApiErrorEnum.LockedOut
        ) {
          // User is temporarily locked out after too many failures
          const value = err.response?.value ?? err.response;
          const minutes =
            value?.lockoutMinutesRemaining ?? err.response?.lockoutMinutesRemaining ?? 0;
          setFilePasswordLockoutInfo(fileId, minutes);
          setError(t('errors.filePasswordLockout', { minutes }));
        } else if (getApiErrorCode(err.response)) {
          setError(resolveFolderPasswordApiErrorMessage(err, t));
        } else {
          setError(t('modal.enterFolderPasswordForm.genericError'));
        }
        // Refocus and select the input for quick retry after error
        setTimeout(() => {
          const input = passwordInputRef.current?.input ?? passwordInputRef.current;
          if (input && typeof input.focus === 'function') {
            input.focus();
            if (typeof input.select === 'function') input.select();
          }
        }, 0);
      } finally {
        setIsModalLoading(false);
      }
    } catch {
      // If validation fails, AntD will show feedback - ensure loading resets
      setIsModalLoading(false);
    }
  }, [
    form,
    fileId,
    passwordName,
    onSubmit,
    onSubmitWithPassword,
    onSubmitAfterVerify,
    setIsModalLoading,
    t,
    clearFilePasswordLockoutInfo,
    setFilePasswordLockoutInfo,
  ]);

  const handleForgotPasswordClick = useCallback(() => {
    const resetModalInstance = modal.confirm({
      content: (
        <ResetFilePasswordForm
          folderId={fileId}
          onSuccess={() => {
            resetModalInstance.destroy();
            onCloseParentModal?.();
          }}
          onClose={() => resetModalInstance.destroy()}
        />
      ),
      footer: null,
      title: t('modal.enterFolderPasswordForm.resetPassword'),
      width: 480,
    });
  }, [fileId, modal, onCloseParentModal, t]);

  /**
   * Handles form cancellation (resets state and fields, fires onCancel if provided)
   */
  const handleCancel = useCallback(() => {
    form.resetFields();
    setError(null);
    onCancel?.();
  }, [form, onCancel]);

  // Show appropriate error or lockout message if any
  const messageToShow =
    isLockedOut && filePasswordLockoutInfo?.fileId === fileId
      ? t('errors.filePasswordLockout', {
          minutes: filePasswordLockoutInfo.minutesRemaining ?? 0,
        })
      : error;

  return (
    <div aria-label={t('a11y.enterFilePasswordForm')}>
      {/* Show any additional description/content if passed */}
      {content}
      <Form
        form={form}
        layout="vertical"
        size="large"
        className="pt-6! [&_.ant-form-item]:mb-2! [&_.ant-form-item-explain-error]:hidden! [&_.ant-form-item-label]:hidden!"
        onFinish={handleConfirm}
      >
        {/* Password input field with icon, disables if loading or locked out */}
        <Form.Item
          className="pt-4!"
          name={passwordName}
          status={messageToShow ? 'error' : undefined}
          rules={[
            {
              message: t('modal.enterFolderPasswordForm.required'),
              required: true,
            },
          ]}
        >
          <Input.Password
            ref={passwordInputRef}
            disabled={isModalLoading || isLockedOut}
            autoFocus={autoFocus}
            prefix={<Icon name="KeyIcon" className="h-5 w-5" />}
            placeholder={
              passwordPlaceholder ?? t('modal.enterFolderPasswordForm.passwordPlaceholder')
            }
            onPressEnter={handleConfirm}
            className="w-full"
          />
        </Form.Item>

        {/* Visual error/lockout feedback below input */}
        <p className="text-red mb-2 h-3 w-full text-center text-sm" role="alert">
          {messageToShow}
        </p>

        {isOwner && (
          <div className="mb-2 flex justify-center">
            <Button
              type="link"
              size="small"
              className="text-blue-dark! hover:text-azure! p-0"
              onClick={handleForgotPasswordClick}
            >
              {t('modal.enterFolderPasswordForm.forgotPassword')}
            </Button>
          </div>
        )}

        {/* Footer: Confirm/Cancel buttons, right-aligned */}
        <div className="mt-12 flex justify-end gap-2">
          {/* Cancel button */}
          <Button
            size="middle"
            onClick={handleCancel}
            aria-label={cancelLabel ?? t('modal.enterFolderPasswordForm.cancel')}
            loading={isModalLoading}
            disabled={isModalLoading}
          >
            {cancelLabel ?? t('modal.enterFolderPasswordForm.cancel')}
          </Button>
          {/* Confirm/Submit button */}
          <Button
            type="primary"
            htmlType="submit"
            size="middle"
            aria-label={confirmLabel ?? t('modal.enterFolderPasswordForm.confirm')}
            loading={isModalLoading}
            disabled={isModalLoading || isLockedOut}
          >
            {confirmLabel ?? t('modal.enterFolderPasswordForm.confirm')}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default EnterFilePasswordForm;
